import { useEffect, useRef, useState } from "react";
import { RectangleVertical, Trash2 } from "lucide-react";
import GameCard from "@/components/game/GameCard";
import Coin from "@/components/game/Coin";
import CitySizeTrack, { PopulationBreakdown } from "@/components/game/CitySizeTrack";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PLAYER_COLORS, PLAYER_ON_COLOR } from "@/lib/api";
import { CARD_ASPECT } from "@/lib/artAssets";
import { ArtCropPortrait } from "@/components/game/CardArt";
import { BACK_COLORS, BackArt } from "@/components/game/Piles";
import { useStageLayout } from "@/components/game/Stage";

const ICON = { size: 15, strokeWidth: 1.8 };
const POP_STYLE = { background: "var(--card)", borderColor: "var(--line)", boxShadow: "var(--shadow-card)" };

// Face-up hand of another player, on demand (only when "show all hands" is on).
function HandThumbs({ player, onZoomCard }) {
  return (
    <div className="grid grid-cols-4 gap-2" data-testid={`player-hand-thumbs-${player.color}`}>
      {player.hand.map((card, i) => (
        <div key={card.id} style={{ aspectRatio: CARD_ASPECT }}>
          <GameCard card={card} size="fit" onClick={() => onZoomCard(card)} testid={`thumb-card-${player.color}-${i}-${card.kind}`} />
        </div>
      ))}
    </div>
  );
}

function HandPeek({ player, onZoomCard, children }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="btn-ghost -my-3 -ml-2 px-2" style={{ fontSize: 13 }} data-testid={`player-hand-peek-${player.color}`} aria-label={`Show ${player.name}'s cards`}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] p-3" style={POP_STYLE}>
        <div className="section-label mb-2">{`${player.name}'s cards`}</div>
        <HandThumbs player={player} onZoomCard={onZoomCard} />
      </PopoverContent>
    </Popover>
  );
}

// Action cards held face down beside the player (max 3, action_cards_max): face up only to their owner. 15x24 = card ratio 900x1440.
const MINI = { width: 15, height: 24, borderRadius: 3 };
const MICRO = { width: 12, height: 19, borderRadius: 2.5 };
function HeldActionCards({ player, cards, count, max = 3, faceUp, selectedId, onClick, onHover, small = false }) {
  const n = faceUp ? cards.length : count;
  const box = small ? MICRO : MINI;
  return (
    <span data-testid={`player-action-cards-${player.color}`} data-count={n} className="shrink-0 inline-flex items-center" style={{ gap: 3 }} title={`${n} action card${n === 1 ? "" : "s"} (max ${max})`}>
      {Array.from({ length: max }, (_, i) => {
        if (i >= n) return <span key={i} aria-hidden="true" style={{ ...box, border: "1px dashed #C9C3B6" }} />;
        if (!faceUp) {
          return (
            <span key={i} data-testid={`held-action-${player.color}-${i}`} data-face="down" className="relative overflow-hidden" style={{ ...box, background: BACK_COLORS.action.face, boxShadow: "inset 0 0 0 1.5px var(--card)" }}>
              <BackArt kind="action" radius={box.borderRadius} />
            </span>
          );
        }
        const card = cards[i];
        const sel = card.id === selectedId;
        return (
          <button
            key={card.id}
            type="button"
            data-testid={`held-action-${player.color}-${i}`}
            data-face="up"
            data-card-key={card.key}
            aria-pressed={sel}
            aria-label={`${card.name} (your action card)`}
            title={card.name}
            onClick={() => onClick?.(card, i)}
            onMouseEnter={() => onHover?.(card)}
            onMouseLeave={() => onHover?.(null)}
            className="relative overflow-hidden p-0 border-0 cursor-pointer"
            style={{ ...box, background: "var(--card)", boxShadow: sel ? "0 0 0 2px var(--sea)" : "0 0 0 1px var(--card-border)" }}
          >
            <ArtCropPortrait card={card} width={box.width} height={box.height} />
          </button>
        );
      })}
    </span>
  );
}

// "Discard action card (n left this game)": in the TURN CONTROLS panel (bottom row). Pick one, confirm.
// actionDiscard: { left, limit, enabled, onDiscard(index) }. Disabled while no action card is held.
export function DiscardActionCard({ player, cards, actionDiscard }) {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(null);
  const { left, limit, enabled, onDiscard } = actionDiscard;
  const label = `Discard action card (${left} left this game)`;
  useEffect(() => {
    if (!open) setPick(null);
  }, [open]);
  const chosen = pick !== null ? cards[pick] : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`discard-action-open-${player.color}`}
          data-left={left}
          disabled={!enabled || cards.length === 0}
          aria-label={label}
          title={left <= 0 ? `No action card discards left this game (${limit} used)` : cards.length === 0 ? "You hold no action cards" : label}
          className="btn-ghost min-w-0 max-w-full inline-flex items-center"
          style={{ height: 28, padding: "0 6px", gap: 4, fontSize: 12, fontWeight: 600, color: enabled && cards.length > 0 ? "var(--ink-2)" : "var(--ink-5)" }}
        >
          <Trash2 size={13} strokeWidth={1.8} className="shrink-0" />
          <span className="truncate">
            Discard action card (<span data-testid={`discard-action-left-${player.color}`}>{left}</span> left this game)
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-[250px] p-3" style={POP_STYLE} data-testid="discard-action-popover" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div data-testid="discard-action-title" style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)" }}>{label}</div>
        <div className="mt-0.5" style={{ fontSize: 11.5, lineHeight: "15px", color: "var(--ink-3)" }}>Free, no replacement card. Pick the card to discard:</div>
        <div className="mt-2 flex flex-col" style={{ gap: 4 }}>
          {cards.map((card, i) => (
            <button
              key={card.id}
              type="button"
              data-testid={`discard-action-pick-${i}`}
              aria-pressed={pick === i}
              onClick={() => setPick(i)}
              className="list-row-lift flex items-center gap-2 text-left"
              style={{ padding: "4px 6px", borderRadius: 6, background: "var(--card)", border: `1px solid ${pick === i ? "var(--sea)" : "var(--line)"}`, boxShadow: pick === i ? "0 0 0 1px var(--sea)" : "none" }}
            >
              <span className="relative overflow-hidden shrink-0" style={{ ...MINI }}>
                <ArtCropPortrait card={card} width={MINI.width} height={MINI.height} />
              </span>
              <span className="truncate" style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{card.name}</span>
            </button>
          ))}
        </div>
        <div className="mt-2.5 flex" style={{ gap: 6 }}>
          <button
            type="button"
            data-testid="discard-action-confirm"
            disabled={!chosen || !enabled}
            onClick={() => {
              onDiscard(pick);
              setOpen(false);
            }}
            className="btn-primary btn-compact btn-card flex-1"
          >
            {chosen ? `Discard ${chosen.name}` : "Discard"}
          </button>
          <button type="button" data-testid="discard-action-cancel" onClick={() => setOpen(false)} className="btn-secondary btn-compact btn-card">
            Cancel
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SeatBadge({ player, seat, size = 24 }) {
  return (
    <span
      data-testid={`player-seat-${player.color}`}
      className="shrink-0 inline-flex items-center justify-center"
      style={{ width: size, height: size, borderRadius: 6, background: PLAYER_COLORS[player.color], color: PLAYER_ON_COLOR[player.color], fontSize: size > 22 ? 13 : 12, fontWeight: 700 }}
    >
      {seat}
    </span>
  );
}

function Money({ player, money }) {
  return (
    <span data-testid={`player-money-${player.color}`} className="inline-flex items-center gap-1.5" title="Money">
      <Coin size={15} />
      <span data-testid={`player-money-value-${player.color}`} style={{ fontWeight: 600, color: "var(--ink)" }}>{money}</span>
    </span>
  );
}

function Debt({ player, short = false }) {
  if (!(player.debt > 0)) return null;
  return (
    <span data-testid={`player-debt-${player.color}`} className="inline-flex items-center gap-1" title={`Debt: ${player.debt} money. Pay off your debt before playing cards.`}>
      {!short && <Coin size={15} standalone />}
      {short ? "" : "debt "}
      <span style={{ color: "var(--danger)", fontWeight: 600 }}>{short ? `−${player.debt}` : player.debt}</span>
    </span>
  );
}

// Resilience is public information and shown live; the full-game score is in the title and the dialog.
function ScoreButton({ player, score, resilience, onOpenScore, label = true }) {
  if (score === undefined) return null;
  const shown = resilience === undefined ? score : resilience;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpenScore?.();
      }}
      data-testid={`player-score-${player.color}`}
      title={`Resilience ${shown}${resilience === undefined ? "" : ` · score if 12 rounds ${score} (population + resilience − debt)`} — click for the breakdown`}
      aria-label={`Resilience ${shown}`}
      className="btn-ghost shrink-0 gap-1"
      style={{ height: 30, minWidth: 30, padding: "0 6px", margin: "-4px -6px -4px 0" }}
    >
      {label && <span style={{ fontSize: 12, color: "var(--ink-4)" }}>{resilience === undefined ? "Score" : "Resilience"}</span>}
      <span style={{ fontSize: 15, fontWeight: 700, color: shown < 0 ? "var(--danger)" : "var(--ink)" }}>{shown}</span>
    </button>
  );
}

function CubesLeft({ player }) {
  return (
    <span className="inline-flex items-center gap-1" data-testid={`player-cubes-left-${player.color}`} title="Starting cubes left">
      <RectangleVertical {...ICON} color="var(--ink-3)" />
      {`${player.starting_cubes_remaining ?? 0} cube${player.starting_cubes_remaining === 1 ? "" : "s"} left`}
    </span>
  );
}

function useHoverOpen() {
  const [open, setOpen] = useState(false);
  const pinned = useRef(false);
  const timer = useRef(null);
  const anchor = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const enter = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };
  const leave = () => {
    clearTimeout(timer.current);
    if (!pinned.current) timer.current = setTimeout(() => setOpen(false), 160);
  };
  // Click pins the details open; a second click (or outside click / Esc) closes them.
  const toggle = () => {
    clearTimeout(timer.current);
    const next = !(pinned.current && open);
    pinned.current = next;
    setOpen(next);
  };
  const onOpenChange = (v) => {
    if (!v) pinned.current = false;
    setOpen(v);
  };
  // A click on the row itself is handled by toggle, not as an outside click.
  const onPointerDownOutside = (e) => {
    if (anchor.current?.contains(e.target)) e.preventDefault();
  };
  return { open, enter, leave, toggle, onOpenChange, onPointerDownOutside, anchor };
}

// Other players: one compact row each — seat, name, money, score, held action cards.
function PlayerRow(props) {
  const { player, seat, active, mode, score, resilience, money, cards, onOpenScore, showHand, onZoomCard, online, waterNote, economy, brackets, actionMax, infoStyle } = props;
  const isSetup = mode === "setup";
  const { compact } = useStageLayout();
  const hover = useHoverOpen();
  const count = player.action_card_count ?? (player.action_cards || []).length;
  return (
    <Popover open={hover.open} onOpenChange={hover.onOpenChange}>
      <PopoverAnchor asChild>
        <div
          ref={hover.anchor}
          data-testid={`player-panel-${player.color}`}
          data-active={active ? "true" : "false"}
          data-expanded="false"
          role="button"
          tabIndex={0}
          aria-label={`${player.name}: details`}
          aria-expanded={hover.open}
          onMouseEnter={hover.enter}
          onMouseLeave={hover.leave}
          onClick={hover.toggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              hover.toggle();
            }
          }}
          className="relative flex items-center cursor-pointer"
          style={{
            gap: 8,
            minHeight: 34,
            padding: compact ? "4px 10px" : "4px 12px",
            borderRadius: "var(--radius)",
            background: hover.open ? "rgba(255,255,255,0.55)" : "transparent",
            border: `1px solid ${active ? "#CFC9BC" : "transparent"}`,
            transition: "background-color 150ms ease",
            ...infoStyle,
          }}
        >
          <SeatBadge player={player} seat={seat} size={22} />
          <span className="truncate min-w-0" style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", flex: "0 1 auto" }}>{player.name}</span>
          {active && (
            <span data-testid={`player-turn-${player.color}`} data-marker="playing" className="shrink-0" style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--sea)" }}>
              playing
            </span>
          )}
          <span className="flex-1" />
          {!isSetup && <HeldActionCards player={player} cards={[]} count={count} max={actionMax} faceUp={false} small />}
          <span className="shrink-0 inline-flex items-center" style={{ gap: 6, fontSize: 13, color: "var(--ink-2)" }}>
            {isSetup ? <CubesLeft player={player} /> : <Money player={player} money={money} />}
            <Debt player={player} short />
          </span>
          {!isSetup && <ScoreButton player={player} score={score} resilience={resilience} onOpenScore={onOpenScore} label={false} />}
          {waterNote && (
            <span
              data-testid={`player-water-summary-${player.color}`}
              title={waterNote}
              className="absolute truncate animate-in fade-in duration-150 flex items-center"
              style={{ left: 40, right: 8, top: 0, bottom: 0, fontSize: 12, color: "var(--ink-2)", background: "var(--table)" }}
            >
              {waterNote}
            </span>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={10}
        onMouseEnter={hover.enter}
        onMouseLeave={hover.leave}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={hover.onPointerDownOutside}
        className="w-[280px] p-3"
        style={POP_STYLE}
        data-testid={`player-details-${player.color}`}
      >
        <div className="flex items-center gap-2 mb-2">
          <SeatBadge player={player} seat={seat} size={20} />
          <span className="truncate" style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{player.name}</span>
          {score !== undefined && !isSetup && <span className="ml-auto" style={{ fontSize: 13, color: "var(--ink-2)" }}>{resilience !== undefined && <>Resilience <b style={{ color: "var(--ink)" }}>{resilience}</b> · </>}Score <b style={{ color: "var(--ink)" }}>{score}</b></span>}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2" style={{ fontSize: 13, color: "var(--ink-2)" }}>
          <span className="inline-flex items-center gap-1.5"><Coin size={14} /><b style={{ color: "var(--ink)" }}>{money}</b> money</span>
          {player.debt > 0 && <span>debt <b style={{ color: "var(--danger)" }}>{player.debt}</b></span>}
          {isSetup && <span>{player.starting_cubes_remaining ?? 0} cube{player.starting_cubes_remaining === 1 ? "" : "s"} left</span>}
          {online && !isSetup && <span data-testid={`player-hand-count-${player.color}`}>{cards} card{cards === 1 ? "" : "s"} in hand</span>}
        </div>
        {economy && !isSetup && (
          <div data-testid={`player-income-${player.color}`} className="mb-2" style={{ fontSize: 12.5, color: "var(--ink-2)" }}>
            Income <b style={{ color: "var(--ink)" }}>+{economy.round_income ?? economy.income}</b> per round
            <span style={{ color: "var(--ink-3)" }}>
              {` (${economy.bracket_name} city) · repairs ×${economy.repair_multiplier}`}
            </span>
          </div>
        )}
        <CitySizeTrack color={player.color} economy={economy} brackets={brackets} />
        {economy && !isSetup && <PopulationBreakdown color={player.color} economy={economy} style={{ marginTop: 8 }} />}
        {!isSetup && (
          <div className="mt-2.5 flex items-center gap-2" style={{ fontSize: 12.5, color: "var(--ink-2)" }}>
            <HeldActionCards player={player} cards={[]} count={count} max={actionMax} faceUp={false} />
            <span>{count} action card{count === 1 ? "" : "s"} held (face down)</span>
          </div>
        )}
        {showHand && !isSetup && player.hand?.length > 0 && (
          <div className="mt-3">
            <div className="section-label mb-1.5">{`${player.name}'s cards`}</div>
            <HandThumbs player={player} onZoomCard={onZoomCard} />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Active player (or this screen's own seat): name, money, score, city-size track, held action
// cards (face up only to their owner). The turn controls live in the right column now.
function PlayerCard(props) {
  const { player, seat, active, mode, score, resilience, money, cards, onOpenScore, showHand, onZoomCard, online, waterNote, economy, brackets, actionMax, infoStyle } = props;
  const isSetup = mode === "setup";
  const { compact } = useStageLayout();
  const pad = compact ? 11 : 12;
  const isMe = !!online && player.color === online.myColor;
  return (
    <div
      data-testid={`player-panel-${player.color}`}
      data-active={active ? "true" : "false"}
      data-expanded="true"
      className="relative flex flex-col shrink-0"
      style={{ gap: 7, padding: pad, borderRadius: "var(--radius)", background: "var(--card)", border: `1px solid ${active ? "#CFC9BC" : "var(--line)"}` }}
    >
      <div className="relative flex flex-col" style={{ gap: 7, ...infoStyle }}>
        <div className="flex items-center gap-2.5 min-w-0" style={{ minHeight: 24 }}>
          <SeatBadge player={player} seat={seat} />
          <span className="truncate" style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>{player.name}</span>
          {!isSetup && (
            <span className="ml-auto inline-flex">
              <HeldActionCards
                player={player}
                cards={[]}
                count={player.action_card_count ?? (player.action_cards || []).length}
                max={actionMax}
                faceUp={false}
              />
            </span>
          )}
          {!isSetup && <ScoreButton player={player} score={score} resilience={resilience} onOpenScore={onOpenScore} label={!compact} />}
        </div>
        {waterNote && (
          <span
            data-testid={`player-water-summary-${player.color}`}
            title={waterNote}
            className="absolute line-clamp-2 animate-in fade-in duration-150"
            style={{ left: 0, right: 0, top: 32, fontSize: 12, lineHeight: "15px", color: "var(--ink-2)" }}
          >
            {waterNote}
          </span>
        )}
        <div className="flex items-center gap-x-3.5 gap-y-1 flex-wrap" style={{ fontSize: 13, color: "var(--ink-2)", minHeight: 20, visibility: waterNote ? "hidden" : "visible" }}>
          <Money player={player} money={money} />
          {isSetup && <CubesLeft player={player} />}
          {showHand && !isSetup && cards > 0 && (
            <HandPeek player={player} onZoomCard={onZoomCard}>
              <RectangleVertical {...ICON} color="var(--ink-3)" />
            </HandPeek>
          )}
          {online && !isMe && !isSetup && (
            <span data-testid={`player-hand-count-${player.color}`} className="inline-flex items-center gap-1" title={`${cards} card${cards === 1 ? "" : "s"} in hand (face down)`}>
              <RectangleVertical {...ICON} color="var(--ink-3)" />
              {cards}
            </span>
          )}
          <Debt player={player} />
        </div>
        <CitySizeTrack color={player.color} economy={economy} brackets={brackets} breakdown={isSetup ? "title" : "hover"} />
      </div>
    </div>
  );
}

export default function PlayerPanel({ expanded = false, incoming = 0, ...props }) {
  const money = Math.max(0, (props.player.money ?? 0) - incoming);
  const cards = props.player.hand_count ?? props.player.hand.length;
  return expanded ? <PlayerCard {...props} money={money} cards={cards} /> : <PlayerRow {...props} money={money} cards={cards} />;
}
